import { GateResolver } from '../path-finding';
import { PathNode, UNASSIGNED_REGION_ID } from './PathNode';

/**
 * Coarse reachability graph used as an early-out for pathfinding.
 *
 * The path-node graph is partitioned into "regions" by treating any edge to a
 * gate-bearing node (one with a non-empty `reference`) as a *gated* edge.
 * Open edges (no gate involved) are walked with BFS to assign each node a
 * `regionId`. Gated edges become entries in a small meta-graph, tagged with
 * the gate reference.
 *
 * Reachability queries do a tiny BFS over this meta-graph respecting the
 * current `GateResolver` state. Typical region counts are tens, so queries
 * are effectively O(1) compared to the per-call cost of A*.
 *
 * Limitations:
 * - Build runs once after navmesh load. Adding/removing path nodes requires
 *   a rebuild.
 * - Gate *state* changes do not require a rebuild — only path-node topology
 *   changes do.
 */
export class RegionGraph {
  /** Number of regions discovered. */
  public regionCount: number = 0;

  /** Adjacency: regionId -> list of {neighbour regionId, gate reference}. */
  private metaEdges: Map<number, MetaEdge[]> = new Map();

  /**
   * Build the region graph from a list of path nodes. Mutates each node's
   * `regionId` field. Safe to call multiple times — clears prior state first.
   */
  public build(nodes: PathNode[]): void {
    this.regionCount = 0;
    this.metaEdges = new Map();

    for (const node of nodes) {
      node.regionId = UNASSIGNED_REGION_ID;
    }

    // BFS over open edges. An edge between A and B is "open" iff neither A
    // nor B has a gate reference. Any edge touching a gated node becomes a
    // meta-edge instead.
    for (const seed of nodes) {
      if (seed.regionId !== UNASSIGNED_REGION_ID) continue;
      if (this.isGated(seed)) continue;

      const regionId = this.regionCount++;
      let head = 0;
      const queue: PathNode[] = [seed];
      seed.regionId = regionId;

      while (head < queue.length) {
        const current = queue[head++];
        for (const neighbour of current.neighbours) {
          if (this.isGated(neighbour)) continue;
          if (neighbour.regionId !== UNASSIGNED_REGION_ID) continue;
          neighbour.regionId = regionId;
          queue.push(neighbour);
        }
      }
    }

    // Each gated node forms its own singleton region. (A cluster of
    // contiguous gated nodes sharing the same reference could collapse into
    // one region, but we keep them separate for simplicity — the meta-graph
    // BFS handles chains naturally.)
    for (const node of nodes) {
      if (node.regionId === UNASSIGNED_REGION_ID) {
        node.regionId = this.regionCount++;
      }
    }

    // Build meta-edges. For every edge (a -> b) where the regions differ,
    // record a meta-edge from a.regionId to b.regionId tagged with the gate
    // reference of whichever endpoint is gated. If both are gated we record
    // both references — traversal will require both gates.
    const seen = new Set<string>();
    for (const node of nodes) {
      for (const neighbour of node.neighbours) {
        if (node.regionId === neighbour.regionId) continue;

        const key = `${node.regionId}->${neighbour.regionId}:${neighbour.reference}|${node.reference}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const gateRefs: string[] = [];
        if (node.reference) gateRefs.push(node.reference);
        if (neighbour.reference) gateRefs.push(neighbour.reference);

        this.addMetaEdge(node.regionId, neighbour.regionId, gateRefs);
      }
    }
  }

  /**
   * Returns true if `goal` is reachable from `start` given the current gate
   * state. False indicates the goal is unreachable and A* should not be run.
   */
  public isReachable(start: PathNode, goal: PathNode, gateResolver: GateResolver): boolean {
    if (start.regionId === UNASSIGNED_REGION_ID) return true; // not built — be permissive
    if (start.regionId === goal.regionId) return true;

    const visited = new Set<number>([start.regionId]);
    let head = 0;
    const queue: number[] = [start.regionId];

    while (head < queue.length) {
      const current = queue[head++];
      const edges = this.metaEdges.get(current);
      if (!edges) continue;

      for (const edge of edges) {
        if (visited.has(edge.to)) continue;
        if (!this.gatesOpen(edge.gateRefs, gateResolver)) continue;

        if (edge.to === goal.regionId) return true;

        visited.add(edge.to);
        queue.push(edge.to);
      }
    }

    return false;
  }

  private isGated(node: PathNode): boolean {
    return node.reference != null && node.reference !== '';
  }

  private addMetaEdge(from: number, to: number, gateRefs: string[]): void {
    let edges = this.metaEdges.get(from);
    if (!edges) {
      edges = [];
      this.metaEdges.set(from, edges);
    }
    edges.push({ to, gateRefs });
  }

  private gatesOpen(gateRefs: string[], gateResolver: GateResolver): boolean {
    for (const ref of gateRefs) {
      if (!gateResolver.testGate(ref)) return false;
    }
    return true;
  }
}

type MetaEdge = {
  to: number;
  gateRefs: string[];
};
